import { Logo } from "../components/Logo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { formatCurrency } from "../lib/api";
import { PartnerLandingSection } from "../components/Partner";
import {
  Stethoscope, Calculator, FilePdf, Users, Package, ListChecks, UploadSimple, Check, X, ArrowRight, Clock, ShieldCheck, Sparkle,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay },
});

function Header() {
  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/60" data-testid="header-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={36} />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
          <a href="#funcionalidades" className="hover:text-indigo-600 transition-colors">Funcionalidades</a>
          <a href="#precos" className="hover:text-indigo-600 transition-colors">Preços</a>
          <a href="#faq" className="hover:text-indigo-600 transition-colors">Dúvidas</a>
        </nav>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" className="rounded-full"><Link to="/login" data-testid="landing-login">Entrar</Link></Button>
          <Button asChild className="rounded-full bg-indigo-600 hover:bg-indigo-700"><Link to="/register" data-testid="landing-header-cta">Teste grátis</Link></Button>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,rgba(79,70,229,0.12),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(20,184,166,0.12),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fade(0)}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide uppercase text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1.5"><Sparkle size={14} weight="fill" /> Para dentistas, médicos, fisioterapeutas, esteticistas e clínicas</span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Descubra o valor exato a cobrar por cada procedimento ou sessão.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            O Seu Precificador calcula sua hora clínica, rateia aluguel, equipe e materiais, e gera orçamentos em PDF prontos para o paciente. Serve para qualquer profissional da saúde — chega de chutar preço.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-12 px-7 text-base"><Link to="/register" data-testid="hero-cta">Começar teste grátis de 21 dias <ArrowRight size={18} className="ml-2" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 text-base"><a href="#como-funciona" data-testid="hero-secondary">Ver como funciona</a></Button>
          </div>
          <p className="mt-4 text-sm text-slate-500 flex items-center gap-2"><ShieldCheck size={16} className="text-teal-600" /> Sem cartão de crédito. Cancele quando quiser.</p>
        </motion.div>
        <motion.div {...fade(0.15)} className="relative">
          <div className="absolute -inset-4 bg-indigo-100/60 rounded-3xl rotate-2 -z-10" />
          <img src="https://images.pexels.com/photos/5355853/pexels-photo-5355853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Dentista usando tablet" className="rounded-2xl shadow-[0_8px_30px_-4px_rgba(79,70,229,0.2)] w-full object-cover aspect-[4/3]" />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg border border-slate-200/60 p-4 w-56" data-testid="hero-float-card">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Sua hora clínica</div>
            <div className="font-mono-nums text-2xl font-extrabold text-slate-900">R$ 248,50</div>
            <div className="text-xs text-teal-700 mt-1">calculada automaticamente</div>
          </div>
          <div className="absolute -top-5 -right-4 bg-white rounded-2xl shadow-lg border border-slate-200/60 p-4 w-52">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Restauração 2 faces</div>
            <div className="font-mono-nums text-xl font-extrabold text-indigo-700">R$ 312,00</div>
            <div className="text-xs text-slate-500 mt-1">preço ideal · margem 100%</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Calculator, title: "Cadastre seus custos", text: "Aluguel, equipe, contas, materiais e quantas horas você atende por mês. O sistema calcula sua hora clínica." },
  { icon: Stethoscope, title: "Monte os procedimentos", text: "Tempo de atendimento, materiais usados e profissional. Ou importe nossa biblioteca com mais de 100 procedimentos prontos." },
  { icon: FilePdf, title: "Gere orçamentos em PDF", text: "Preço ideal, mínimo e aproximado em segundos. Orçamento profissional com a logo da sua clínica." },
];

function HowItWorks() {
  return (
    <section id="como-funciona" className="py-20 md:py-32 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fade(0)} className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight text-slate-900">Como funciona</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600">Três passos para saber exatamente quanto cobrar — e provar isso ao paciente.</p>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-8 md:gap-12 relative">
          <div className="hidden md:block absolute top-8 left-[16%] right-[16%] border-t-2 border-dashed border-indigo-200" />
          {STEPS.map((s, i) => (
            <motion.div key={s.title} {...fade(i * 0.1)} className="relative">
              <div className="w-16 h-16 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-200 relative z-10"><s.icon size={28} weight="duotone" /></div>
              <div className="mt-5 text-xs font-semibold text-indigo-600 tracking-wider">PASSO {i + 1}</div>
              <h3 className="mt-1 text-xl font-bold text-slate-800">{s.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Clock, title: "Hora clínica automática", text: "Some seus custos fixos e variáveis, divida pelas horas trabalhadas e descubra o custo real de cada minuto na cadeira.", span: "md:col-span-2", bg: "bg-indigo-50" },
  { icon: Package, title: "Materiais por procedimento", text: "Custo por unidade calculado a partir da embalagem.", span: "", bg: "bg-slate-50" },
  { icon: ListChecks, title: "+100 procedimentos prontos", text: "Biblioteca com tempos médios por especialidade. Importe com um clique.", span: "", bg: "bg-teal-50" },
  { icon: UploadSimple, title: "Importação em lista", text: "Cole do Excel ou envie CSV/XLSX. Materiais, custos e procedimentos em segundos.", span: "", bg: "bg-slate-50" },
  { icon: Users, title: "Vários profissionais", text: "Cada dentista com sua hora e comissão.", span: "", bg: "bg-slate-50" },
  { icon: FilePdf, title: "Orçamento em PDF com sua logo", text: "Documento profissional para o paciente, com preço ideal, descontos e observações.", span: "md:col-span-2", bg: "bg-indigo-50", img: "https://images.pexels.com/photos/5355693/pexels-photo-5355693.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

function Features() {
  return (
    <section id="funcionalidades" className="py-20 md:py-32 bg-slate-50" data-testid="features-grid-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fade(0)} className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight text-slate-900">Tudo o que a planilha não faz</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600">Atualizou o aluguel? Todos os preços recalculam na hora.</p>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} {...fade(i * 0.06)} className={`${f.span} ${f.bg} rounded-2xl border border-slate-200/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex gap-6`} data-testid={`feature-${i}`}>
              <div className="flex-1">
                <div className="w-11 h-11 rounded-xl bg-white border border-slate-200/60 flex items-center justify-center text-indigo-600"><f.icon size={22} weight="duotone" /></div>
                <h3 className="mt-4 text-lg font-bold text-slate-800">{f.title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.text}</p>
              </div>
              {f.img && <img src={f.img} alt="" className="hidden lg:block w-48 rounded-xl object-cover" />}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COMPARE = [
  ["Atualiza todos os preços quando um custo muda", false, true],
  ["Guarda materiais, procedimentos e orçamentos", false, true],
  ["Orçamento em PDF para o paciente", false, true],
  ["Vários profissionais com horas diferentes", false, true],
  ["Acesso de qualquer lugar, no celular", false, true],
  ["Preço mínimo e ideal + limite de desconto", false, true],
  ["Semáforo de margem do preço que você cobra hoje", false, true],
];

function Comparison() {
  return (
    <section className="py-20 md:py-32 bg-white" data-testid="comparison-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fade(0)}>
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight text-slate-900">Planilha e calculadora grátis vs. O Seu Precificador</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600">Calculadoras gratuitas não guardam nada. Planilhas quebram e ninguém atualiza. Aqui, tudo fica vivo e conectado.</p>
          <img src="https://images.pexels.com/photos/5355860/pexels-photo-5355860.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Dentista sorrindo" className="mt-8 rounded-2xl object-cover aspect-video w-full" />
        </motion.div>
        <motion.div {...fade(0.1)} className="rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
          <div className="grid grid-cols-[1fr_auto_auto] text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 px-5 py-3">
            <span>Recurso</span><span className="w-20 text-center">Planilha</span><span className="w-24 text-center text-indigo-700">O Seu Precificador</span>
          </div>
          {COMPARE.map(([label, a, b]) => (
            <div key={label} className="grid grid-cols-[1fr_auto_auto] items-center px-5 py-3.5 border-t border-slate-100 text-sm text-slate-700">
              <span>{label}</span>
              <span className="w-20 flex justify-center">{a ? <Check size={18} className="text-teal-600" weight="bold" /> : <X size={18} className="text-rose-400" weight="bold" />}</span>
              <span className="w-24 flex justify-center">{b ? <Check size={18} className="text-teal-600" weight="bold" /> : <X size={18} className="text-rose-400" weight="bold" />}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Pricing() {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  useEffect(() => { axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data)).catch(() => {}); }, []);
  return (
    <section id="precos" className="py-20 md:py-32 bg-slate-50" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fade(0)} className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight text-slate-900">Planos simples, menos que uma restauração</h2>
          <p className="mt-4 text-base md:text-lg text-slate-600">Comece com 21 dias grátis do plano Profissional. Sem cartão.</p>
          <div className="mt-8 inline-flex rounded-full bg-white border border-slate-200 p-1 text-sm" data-testid="landing-cycle-toggle">
            <button onClick={() => setYearly(false)} className={`px-5 py-2 rounded-full transition-colors ${!yearly ? "bg-indigo-600 text-white font-semibold" : "text-slate-600"}`} data-testid="landing-cycle-monthly">Mensal</button>
            <button onClick={() => setYearly(true)} className={`px-5 py-2 rounded-full transition-colors ${yearly ? "bg-indigo-600 text-white font-semibold" : "text-slate-600"}`} data-testid="landing-cycle-yearly">Anual · 2 meses grátis</button>
          </div>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
          {plans.map((p, i) => (
            <motion.div key={p.id} {...fade(i * 0.08)} className={`relative bg-white rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${p.highlight ? "border-indigo-500 shadow-[0_8px_30px_-4px_rgba(79,70,229,0.2)] xl:scale-105" : "border-slate-200/60 shadow-sm"}`} data-testid={`landing-plan-${p.id}`}>
              {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Mais escolhido</span>}
              <h3 className="text-xl font-bold text-slate-800">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className={`font-extrabold text-slate-900 ${p.contact ? "font-heading text-3xl" : "font-mono-nums text-4xl"}`}>{p.contact ? "Sob consulta" : formatCurrency(yearly ? p.yearly : p.monthly)}</span>
                {!p.contact && <span className="text-sm text-slate-500">/{yearly ? "ano" : "mês"}</span>}
              </div>
              {yearly && !p.contact && <div className="text-xs text-teal-700 mt-1">equivale a {formatCurrency(p.yearly / 12)}/mês</div>}
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
                {p.features.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-teal-600 mt-0.5 shrink-0" weight="bold" />{f}</li>)}
              </ul>
              <Button asChild className={`mt-7 rounded-full h-11 ${p.highlight ? "bg-indigo-600 hover:bg-indigo-700" : p.contact ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}`} variant={p.highlight || p.contact ? "default" : "outline"}>
                {p.contact ? <a href={`https://wa.me/${p.whatsapp}?text=${encodeURIComponent("Olá! Tenho interesse no plano Clínica (personalizado).")}`} target="_blank" rel="noreferrer" data-testid={`landing-plan-cta-${p.id}`}>Falar com consultor</a> : <Link to="/register" data-testid={`landing-plan-cta-${p.id}`}>Testar grátis 21 dias</Link>}
              </Button>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-8">Pagamento seguro via Stripe · cartão ou Pix · cancele quando quiser</p>
      </div>
    </section>
  );
}

const FAQ = [
  ["Preciso de cartão de crédito para testar?", "Não. Você cria a conta e usa o plano Profissional por 21 dias sem informar nenhum cartão. Só assina se gostar."],
  ["Posso cancelar quando quiser?", "Sim. O cancelamento é feito em um clique na área 'Meu Plano' e você mantém acesso até o fim do período já pago."],
  ["Serve para clínicas com vários dentistas?", "Sim. Cada profissional tem seu próprio valor-hora e comissão. O plano Profissional atende até 3 dentistas e o Clínica é ilimitado."],
  ["Como o sistema calcula o preço?", "Somamos seus custos fixos e variáveis mensais, dividimos pelas horas clínicas para achar o custo da hora, e adicionamos materiais, comissão, impostos, taxa de cartão e a margem de lucro desejada."],
  ["Meus dados ficam seguros?", "Sim. Cada clínica vê apenas seus próprios dados, com acesso protegido por senha e criptografia em trânsito."],
  ["Tenho desconto se usar também o Seu Chamador?", "Sim. Assinantes do O Seu Precificador ganham 30% de desconto no Seu Chamador (painel de chamada de pacientes na TV) com o cupom PARCEIRO, e assinantes do Chamador ganham 30% aqui com o código PRECIFICADOR."],
  ["Já tenho uma planilha com meus materiais. Preciso digitar tudo de novo?", "Não. Cole direto do Excel ou envie o arquivo CSV/XLSX e importe tudo de uma vez."],
];

function Faq() {
  return (
    <section id="faq" className="py-20 md:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.h2 {...fade(0)} className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight text-slate-900">Dúvidas frequentes</motion.h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-base font-semibold text-slate-800">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="py-20 md:py-28 bg-indigo-700 relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#818cf8,transparent_40%),radial-gradient(circle_at_80%_80%,#14b8a6,transparent_40%)]" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold tracking-tight text-white">Pronto para precificar com lucro e segurança?</h2>
        <p className="mt-4 text-indigo-100 text-base md:text-lg">Em 15 minutos você descobre quanto custa sua hora e quanto deveria cobrar.</p>
        <Button asChild size="lg" className="mt-8 rounded-full bg-white text-indigo-700 hover:bg-indigo-50 h-12 px-8 text-base font-semibold"><Link to="/register" data-testid="final-cta">Criar minha conta grátis <ArrowRight size={18} className="ml-2" /></Link></Button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400 py-12" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-4 gap-8 text-sm">
        <div className="md:col-span-2">
          <Logo size={32} dark tagline />
          <p className="mt-3 max-w-sm">Precificação inteligente para profissionais e clínicas da saúde.</p>
        </div>
        <div>
          <div className="text-white font-semibold mb-3">Produto</div>
          <ul className="space-y-2"><li><a href="#funcionalidades" className="hover:text-white">Funcionalidades</a></li><li><a href="#precos" className="hover:text-white">Preços</a></li><li><a href="#faq" className="hover:text-white">Dúvidas</a></li></ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-3">Conta</div>
          <ul className="space-y-2"><li><Link to="/login" className="hover:text-white">Entrar</Link></li><li><Link to="/register" className="hover:text-white">Criar conta</Link></li><li><Link to="/forgot-password" className="hover:text-white">Esqueci a senha</Link></li></ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 pt-6 border-t border-slate-800 text-xs">© {new Date().getFullYear()} O Seu Precificador. Todos os direitos reservados.</div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-slate-50 text-slate-900" data-testid="landing-page">
      <Header />
      <Hero />
      <HowItWorks />
      <Features />
      <Comparison />
      <Pricing />
      <PartnerLandingSection />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}
